/**
 * API Cache Utility
 * 
 * Provides centralized caching for API calls to prevent duplicate requests.
 * Features:
 * - In-memory cache with TTL (time-to-live)
 * - Request deduplication (prevents multiple in-flight requests for same endpoint)
 * - Automatic cache invalidation after TTL expires
 * - Manual cache clearing when needed
 */

// Cache storage
const cache = new Map();

// In-flight request tracking (for deduplication)
const inFlightRequests = new Map();

// Default TTL: 5 minutes (in milliseconds)
const DEFAULT_TTL = 5 * 60 * 1000;

/**
 * Generate a cache key from endpoint and params
 */
export const getCacheKey = (endpoint, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join('&');
  return `${endpoint}${sortedParams ? '?' + sortedParams : ''}`;
};

/**
 * Get cached data if it exists and hasn't expired
 */
export const getCachedData = (cacheKey) => {
  const cached = cache.get(cacheKey);
  if (!cached) return null;

  const now = Date.now();
  if (now > cached.expiresAt) {
    // Cache expired, remove it
    cache.delete(cacheKey);
    return null;
  }

  return cached.data;
};

/**
 * Set data in cache with TTL
 */
export const setCachedData = (cacheKey, data, ttl = DEFAULT_TTL) => {
  cache.set(cacheKey, {
    data,
    expiresAt: Date.now() + ttl,
    cachedAt: Date.now(),
  });
};

/**
 * Check if there's an in-flight request for this cache key
 */
export const getInFlightRequest = (cacheKey) => {
  return inFlightRequests.get(cacheKey) || null;
};

/**
 * Register an in-flight request
 */
export const setInFlightRequest = (cacheKey, promise) => {
  inFlightRequests.set(cacheKey, promise);
};

/**
 * Remove in-flight request tracking
 */
export const clearInFlightRequest = (cacheKey) => {
  inFlightRequests.delete(cacheKey);
};

/**
 * Clear specific cache entry
 */
export const clearCacheEntry = (cacheKey) => {
  cache.delete(cacheKey);
};

/**
 * Clear all cache entries matching a pattern
 */
export const clearCacheByPattern = (pattern) => {
  const keysToDelete = [];
  cache.forEach((_, key) => {
    if (key.includes(pattern)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((key) => cache.delete(key));
};

/**
 * Clear all cache entries
 */
export const clearAllCache = () => {
  cache.clear();
  inFlightRequests.clear();
};

/**
 * Wrapper function for making cached API calls
 * 
 * @param {string} cacheKey - Unique key for caching
 * @param {Function} fetchFn - The actual API call function (should return a Promise)
 * @param {Object} options - Options object
 * @param {number} options.ttl - Time to live in milliseconds
 * @param {boolean} options.force - Force refresh, bypassing cache
 * @param {number} options.retries - Number of retries on failure (default: 0)
 * @returns {Promise} - The API response data
 */
export const cachedApiCall = async (cacheKey, fetchFn, options = {}) => {
  const { ttl = DEFAULT_TTL, force = false, retries = 0 } = options;

  // Check cache first (unless force refresh)
  if (!force) {
    const cached = getCachedData(cacheKey);
    if (cached !== null) {
      console.log(`[API Cache] Cache HIT for: ${cacheKey}`);
      return cached;
    }
  }

  // Check for in-flight request
  const inFlight = getInFlightRequest(cacheKey);
  if (inFlight) {
    console.log(`[API Cache] Deduplicating in-flight request for: ${cacheKey}`);
    return inFlight;
  }

  // Make the actual API call with retry logic
  console.log(`[API Cache] Cache MISS, fetching: ${cacheKey}`);

  const executeWithRetry = async (attempt = 0) => {
    try {
      return await fetchFn();
    } catch (error) {
      if (attempt < retries) {
        console.warn(`[API Cache] Request failed, retrying (${attempt + 1}/${retries}): ${cacheKey}`);
        // Linear backoff: 500ms, 1000ms, 1500ms...
        await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        return executeWithRetry(attempt + 1);
      }
      throw error;
    }
  };

  const requestPromise = executeWithRetry()
    .then((data) => {
      // Cache the successful response
      setCachedData(cacheKey, data, ttl);
      return data;
    })
    .finally(() => {
      // Always clean up in-flight tracking
      clearInFlightRequest(cacheKey);
    });

  // Track the in-flight request
  setInFlightRequest(cacheKey, requestPromise);

  return requestPromise;
};

/**
 * Get cache statistics (for debugging)
 */
export const getCacheStats = () => {
  const now = Date.now();
  const entries = [];

  cache.forEach((value, key) => {
    entries.push({
      key,
      expiresIn: Math.max(0, Math.round((value.expiresAt - now) / 1000)) + 's',
      isExpired: now > value.expiresAt,
    });
  });

  return {
    totalEntries: cache.size,
    inFlightRequests: inFlightRequests.size,
    entries,
  };
};

// Predefined cache keys for common endpoints
export const CACHE_KEYS = {
  DOCTOR_RECOMMENDATIONS: (doctorId) => `doctor-recommendations-${doctorId}`,
  DOCTOR_ADVICES: (doctorId) => `doctor-advices-${doctorId}`,
  APPOINTMENT_BY_ID: (appointmentId) => `appointment-${appointmentId}`,
  PRESCRIPTIONS_LATEST: (patientId) => `prescriptions-latest-${patientId}`,
  PRESCRIPTION_BY_ID: (prescriptionId) => `prescription-${prescriptionId}`,
  ADVICE_TRANSLATIONS: (adviceId) => `advice-translations-${adviceId}`,
};

export default {
  getCacheKey,
  getCachedData,
  setCachedData,
  cachedApiCall,
  clearCacheEntry,
  clearCacheByPattern,
  clearAllCache,
  getCacheStats,
  CACHE_KEYS,
};
